import { Link } from 'react-router-dom';
import { Facebook, Instagram, Youtube, Mail, Phone, MapPin, Twitter } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Separator } from '@/shared/components/ui/separator';
import { usePublicSettings } from '@/shared/hooks/useSettings';
import { useNewsletterSubscription } from '@/features/marketing/hooks/useNewsletter';
import { useState } from 'react';
import { toast } from 'sonner';

const Footer: React.FC = () => {
    const { data: settings } = usePublicSettings();
    const newsletterMutation = useNewsletterSubscription();
    const [email, setEmail] = useState('');

    const handleNewsletterSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email.trim()) {
            toast.error('Vui lòng nhập email');
            return;
        }
        try {
            await newsletterMutation.mutateAsync(email);
            setEmail('');
            // Toast is already shown by useNewsletterSubscription hook
        } catch (error) {
            // Error toast is already shown by useNewsletterSubscription hook
        }
    };

    const siteName = settings?.data?.general?.siteName || 'Cosmetic Store';
    const siteDescription = settings?.data?.general?.siteDescription || 'Chuỗi siêu thị mỹ phẩm chính hãng hàng đầu Việt Nam với hàng nghìn sản phẩm từ các thương hiệu nổi tiếng thế giới.';
    const logo = settings?.data?.general?.logo?.url;
    return (
        <footer className="bg-gray-900 text-gray-300">
            {/* Main footer */}
            <div className="container py-12">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                    {/* About */}
                    <div>
                        {logo ? (
                            <img src={logo} alt={siteName} className="h-10 mb-4" />
                        ) : (
                            <h4 className="text-lg font-bold text-white mb-4">{siteName}</h4>
                        )}
                        <p className="text-sm mb-4 leading-relaxed">
                            {siteDescription}
                        </p>
                        <div className="flex gap-3">
                            {settings?.data?.social?.facebook && (
                                <Button variant="ghost" size="icon" className="hover:bg-white/10 text-white" asChild>
                                    <a href={settings.data.social.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                                        <Facebook className="h-5 w-5" />
                                    </a>
                                </Button>
                            )}
                            {settings?.data?.social?.instagram && (
                                <Button variant="ghost" size="icon" className="hover:bg-white/10 text-white" asChild>
                                    <a href={settings.data.social.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                                        <Instagram className="h-5 w-5" />
                                    </a>
                                </Button>
                            )}
                            {settings?.data?.social?.youtube && (
                                <Button variant="ghost" size="icon" className="hover:bg-white/10 text-white" asChild>
                                    <a href={settings.data.social.youtube} target="_blank" rel="noopener noreferrer" aria-label="Youtube">
                                        <Youtube className="h-5 w-5" />
                                    </a>
                                </Button>
                            )}
                            {settings?.data?.social?.twitter && (
                                <Button variant="ghost" size="icon" className="hover:bg-white/10 text-white" asChild>
                                    <a href={settings.data.social.twitter} target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                                        <Twitter className="h-5 w-5" />
                                    </a>
                                </Button>
                            )}
                            {settings?.data?.social?.tiktok && (
                                <Button variant="ghost" size="icon" className="hover:bg-white/10 text-white" asChild>
                                    <a href={settings.data.social.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                                        </svg>
                                    </a>
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Quick links */}
                    <div>
                        <h4 className="text-lg font-bold text-white mb-4">Liên kết nhanh</h4>
                        <ul className="space-y-2 text-sm">
                            <li>
                                <Link to="/pages/ve-chung-toi" className="hover:text-primary transition-colors">
                                    Về chúng tôi
                                </Link>
                            </li>
                            <li>
                                <Link to="/contact" className="hover:text-primary transition-colors">
                                    Liên hệ
                                </Link>
                            </li>
                            <li>
                                <Link to="/products" className="hover:text-primary transition-colors">
                                    Tất cả sản phẩm
                                </Link>
                            </li>
                            <li>
                                <Link to="/products?sale=true" className="hover:text-primary transition-colors">
                                    Khuyến mãi
                                </Link>
                            </li>
                            <li>
                                <Link to="/stores" className="hover:text-primary transition-colors">
                                    Hệ thống cửa hàng
                                </Link>
                            </li>
                            <li>
                                <Link to="/blog" className="hover:text-primary transition-colors">
                                    Blog làm đẹp
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Policies */}
                    <div>
                        <h4 className="text-lg font-bold text-white mb-4">Chính sách</h4>
                        <ul className="space-y-2 text-sm">
                            <li>
                                <Link to="/pages/shipping-return" className="hover:text-primary transition-colors">
                                    Chính sách vận chuyển & Đổi trả
                                </Link>
                            </li>
                            <li>
                                <Link to="/pages/payment-policy" className="hover:text-primary transition-colors">
                                    Chính sách thanh toán
                                </Link>
                            </li>
                            <li>
                                <Link to="/pages/privacy" className="hover:text-primary transition-colors">
                                    Chính sách bảo mật
                                </Link>
                            </li>
                            <li>
                                <Link to="/pages/terms" className="hover:text-primary transition-colors">
                                    Điều khoản dịch vụ
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Contact */}
                    <div>
                        <h4 className="text-lg font-bold text-white mb-4">Liên hệ</h4>
                        <ul className="space-y-3 text-sm">
                            {settings?.data?.general?.address && (
                                <li className="flex items-start gap-2">
                                    <MapPin className="h-4 w-4 mt-1 flex-shrink-0 text-primary" />
                                    <span>{settings.data.general.address}</span>
                                </li>
                            )}
                            {settings?.data?.general?.contactPhone && (
                                <li className="flex items-center gap-2">
                                    <Phone className="h-4 w-4 text-primary" />
                                    <span>
                                        Hotline:{' '}
                                        <a
                                            href={`tel:${settings.data.general.contactPhone}`}
                                            className="text-white font-semibold hover:underline"
                                        >
                                            {settings.data.general.contactPhone}
                                        </a>
                                    </span>
                                </li>
                            )}
                            {settings?.data?.general?.contactEmail && (
                                <li className="flex items-center gap-2">
                                    <Mail className="h-4 w-4 text-primary" />
                                    <a
                                        href={`mailto:${settings.data.general.contactEmail}`}
                                        className="hover:text-primary transition-colors"
                                    >
                                        {settings.data.general.contactEmail}
                                    </a>
                                </li>
                            )}
                        </ul>

                        {/* Newsletter form */}
                        <div className="mt-4">
                            <p className="text-sm mb-2">Đăng ký nhận tin:</p>
                            <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
                                <Input
                                    type="email"
                                    placeholder="Email của bạn"
                                    className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={newsletterMutation.isPending}
                                />
                                <Button size="sm" type="submit" disabled={newsletterMutation.isPending}>
                                    {newsletterMutation.isPending ? '...' : 'Gửi'}
                                </Button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>

            <Separator className="bg-gray-800" />

            {/* Bottom */}
            <div className="container py-4">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm">
                    <p>© {new Date().getFullYear()} {siteName}. All rights reserved.</p>
                    <div className="flex items-center gap-4">
                        <img src="https://upload.wikimedia.org/wikipedia/commons/5/5e/Visa_Inc._logo.svg" alt="Visa" className="h-6 opacity-70" />
                        <img src="https://upload.wikimedia.org/wikipedia/commons/b/b7/MasterCard_Logo.svg" alt="Mastercard" className="h-6 opacity-70" />
                        <span className="text-xs text-gray-500">VNPay | MoMo | COD</span>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
